import React, { useMemo } from "react";

/**
 * CareLogix™ ORACLE — lightweight candlestick chart (Phase 22).
 *
 * Pure inline-SVG. No external libs. Designed for the OANDA FX trading screen.
 * - Green bullish, red bearish candles.
 * - Right-side live price axis with current-price marker.
 * - Subtle horizontal grid.
 *
 * Props:
 *   candles: [{ t, o, h, l, c, v }]
 *   width, height, decimals, lastPrice
 */

const PADDING = { top: 14, right: 64, bottom: 18, left: 8 };
const GRID_COLOR = "rgba(245, 200, 66, 0.10)";
const BULL = "#7AC860";
const BEAR = "#E84A4A";
const AXIS = "#F5C842";
const TEXT_MUTED = "#9aa39a";
const LIVE_LINE = "#F5C842";

function fmt(p, dec) {
  if (typeof p !== "number") return "—";
  return p.toFixed(dec ?? 5);
}

export default function CandlestickChart({
  candles = [],
  width = 1000,
  height = 380,
  decimals = 5,
  lastPrice,
  className = "",
} = {}) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const data = candles && candles.length ? candles : [];

  const view = useMemo(() => {
    if (!data.length) return null;
    let lo = Infinity, hi = -Infinity;
    data.forEach((d) => { lo = Math.min(lo, d.l); hi = Math.max(hi, d.h); });
    if (lastPrice != null) { lo = Math.min(lo, lastPrice); hi = Math.max(hi, lastPrice); }
    const pad = (hi - lo) * 0.08 || (hi * 0.0005 || 0.0001);
    lo -= pad; hi += pad;
    const innerW = Math.max(60, width - PADDING.left - PADDING.right);
    const innerH = Math.max(60, height - PADDING.top - PADDING.bottom);
    const stepX = innerW / data.length;
    const yFor = (p) => PADDING.top + (1 - (p - lo) / (hi - lo)) * innerH;
    const xFor = (i) => PADDING.left + i * stepX + stepX / 2;
    // 5 horizontal grid lines
    const gridLines = [];
    for (let i = 0; i <= 4; i++) {
      const p = lo + (hi - lo) * (i / 4);
      gridLines.push({ y: yFor(p), label: fmt(p, decimals) });
    }
    return { lo, hi, innerW, innerH, stepX, yFor, xFor, gridLines };
  }, [data, lastPrice, width, height, decimals]);

  if (!view) {
    return (
      <div className={`fx-chart fx-chart--empty ${className}`} style={{ height, width: "100%" }} data-testid="fx-chart-empty">
        Loading chart…
      </div>
    );
  }

  const candleBodyW = Math.max(2, Math.min(8, view.stepX * 0.6));

  return (
    <svg
      className={`fx-chart ${className}`}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      width="100%"
      height={height}
      data-testid="fx-chart-svg"
      style={{ display: "block" }}
    >
      {/* Grid + price axis labels */}
      {view.gridLines.map((g, idx) => (
        <g key={idx}>
          <line
            x1={PADDING.left}
            x2={width - PADDING.right}
            y1={g.y}
            y2={g.y}
            stroke={GRID_COLOR}
            strokeDasharray="4 6"
          />
          <text
            x={width - PADDING.right + 6}
            y={g.y + 4}
            fill={AXIS}
            fontSize="11"
            fontFamily="'Space Mono', monospace"
          >
            {g.label}
          </text>
        </g>
      ))}

      {/* Candles */}
      {data.map((c, i) => {
        const x = view.xFor(i);
        const yH = view.yFor(c.h);
        const yL = view.yFor(c.l);
        const yO = view.yFor(c.o);
        const yC = view.yFor(c.c);
        const color = c.c >= c.o ? BULL : BEAR;
        const bodyTop = Math.min(yO, yC);
        const bodyH = Math.max(1.0, Math.abs(yO - yC));
        return (
          <g key={i}>
            <line x1={x} x2={x} y1={yH} y2={yL} stroke={color} strokeWidth={1.0} />
            <rect
              x={x - candleBodyW / 2}
              y={bodyTop}
              width={candleBodyW}
              height={bodyH}
              fill={color}
              stroke={color}
              strokeWidth={0.5}
            />
          </g>
        );
      })}

      {/* Last price line + tag */}
      {lastPrice != null && (
        <g>
          <line
            x1={PADDING.left}
            x2={width - PADDING.right}
            y1={view.yFor(lastPrice)}
            y2={view.yFor(lastPrice)}
            stroke={LIVE_LINE}
            strokeDasharray="3 3"
            strokeWidth={1}
          />
          <rect
            x={width - PADDING.right - 2}
            y={view.yFor(lastPrice) - 9}
            width={PADDING.right - 2}
            height={18}
            fill={LIVE_LINE}
            rx={3}
          />
          <text
            x={width - PADDING.right + 6}
            y={view.yFor(lastPrice) + 4}
            fill="#0c1a10"
            fontSize="11"
            fontWeight="700"
            fontFamily="'Space Mono', monospace"
          >
            {fmt(lastPrice, decimals)}
          </text>
        </g>
      )}

      {/* Bottom timestamp ticks (sparse) */}
      {(() => {
        const ticks = [];
        const N = data.length;
        const stride = Math.max(1, Math.floor(N / 4));
        for (let i = 0; i < N; i += stride) {
          const t = data[i]?.t;
          if (!t) continue;
          const x = view.xFor(i);
          ticks.push(
            <text
              key={`ts-${i}`}
              x={x}
              y={height - 3}
              fill={TEXT_MUTED}
              fontSize="10"
              textAnchor="middle"
              fontFamily="'Space Mono', monospace"
            >
              {t.slice(11, 16)}
            </text>
          );
        }
        return ticks;
      })()}
    </svg>
  );
}
